
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus } from "lucide-react";
import { Dream } from "@/types/database-extensions";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

interface DreamItemProps {
  dream: Dream;
  onDelete: (id: string) => void;
  updateKarmaPoints: (points: number) => Promise<void>;
}

const DreamItem: React.FC<DreamItemProps> = ({ dream, onDelete, updateKarmaPoints }) => {
  const [newBadge, setNewBadge] = useState('');
  const [progress, setProgress] = useState(dream.progress || 0);
  const [badges, setBadges] = useState<string[]>(dream.badges || []);
  const { toast } = useToast();

  const updateProgress = async (id: string, increment: number) => {
    if (!dream) return;
    
    const newProgress = Math.max(0, Math.min(100, progress + increment));
    
    try {
      // Update dream progress
      const { error } = await supabase
        .from('dreams')
        .update({ progress: newProgress })
        .eq('id', id);
      
      if (error) {
        console.error("Error updating progress:", error);
        toast({
          title: "Error",
          description: "Could not update progress",
          variant: "destructive"
        });
        return;
      }
      
      // Update local state
      setProgress(newProgress);
      
      // If progress milestone reached, add karma points
      if ((progress < 50 && newProgress >= 50) || 
          (progress < 100 && newProgress === 100)) {
        await updateKarmaPoints(10);
      }

      toast({
        title: "Progress Updated",
        description: `Progress set to ${newProgress}%`,
      });
    } catch (error) {
      console.error("Error updating progress:", error);
      toast({
        title: "Error",
        description: "Could not update progress",
        variant: "destructive"
      });
    }
  };

  const addBadge = async (dreamId: string) => {
    if (!newBadge.trim()) return;
    
    try {
      const updatedBadges = [...badges, newBadge];
      
      // Add badge
      const { error } = await supabase
        .from('dreams')
        .update({ badges: updatedBadges })
        .eq('id', dreamId);
      
      if (error) {
        console.error("Error adding badge:", error);
        toast({
          title: "Error",
          description: "Could not add milestone",
          variant: "destructive"
        });
        return;
      }
      
      // Update local state
      setBadges(updatedBadges);
      setNewBadge('');

      // Add karma points for milestone
      await updateKarmaPoints(3);
      
      toast({
        title: "Milestone Added",
        description: `Added "${newBadge}" milestone to your dream`,
      });
    } catch (error) {
      console.error("Error adding badge:", error);
      toast({
        title: "Error",
        description: "Could not add milestone",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="p-4 bg-background rounded-md border">
      <div className="flex justify-between items-center">
        <h3 className="font-medium text-lg mb-2">{dream.title}</h3>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => onDelete(dream.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-muted-foreground">Progress</span>
          <span className="text-sm font-medium">{progress}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {badges.length > 0 ? badges.map((badge, index) => (
          <Badge key={index} variant="outline">{badge}</Badge>
        )) : (
          <span className="text-xs text-muted-foreground">No milestones yet</span>
        )}
      </div>
      
      <div className="flex gap-2 mb-3">
        <Input
          placeholder="Add milestone..."
          value={newBadge}
          onChange={(e) => setNewBadge(e.target.value)}
          className="text-sm h-8"
          size={20}
        />
        <Button 
          variant="outline" 
          size="sm" 
          className="h-8"
          onClick={() => addBadge(dream.id)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add
        </Button>
      </div>
      
      <div className="flex justify-end gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => updateProgress(dream.id, -10)}
        >
          -10%
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => updateProgress(dream.id, 10)}
        >
          +10%
        </Button>
      </div>
    </div>
  );
};

export default DreamItem;
